import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PostTypePicker, { type SocialFormatOption } from '../components/PostTypePicker';
import PostPreview, { tryParseCarousel, tryParseThread, tryParsePoll, pollDurationLabel, isSvgSlide } from '../components/PostPreview';
import EvalScoreCard from '../components/EvalScoreCard';

type Step = 'topic' | 'platform' | 'format' | 'result';
type Platform = 'linkedin' | 'x';

interface SocialStudioViewProps {
  brand: string;
  /** Slug of an existing piece to seed the source. When the user picks a
   *  "from past article" topic, we override this internally. */
  pieceSlug: string | null;
  pieces?: Array<{ slug: string; title?: string }>;
  /** Called when the parent should navigate to the Library. */
  onPickPiece?: () => void;
  /** Called when SocialStudio creates a scratch piece behind the scenes for
   *  trending or manual topics. */
  onScratchPieceCreated?: (slug: string) => void;
}

interface CreateResponse {
  slug: string;
  formatId: string;
  rendition: { type: string; content: string; contentHash: string; renderedAt: string };
  evalScore: number | null;
  evalGrade: string | null;
  perFormatScore: { axes?: Record<string, number> } | null;
}

interface SocialTopicSuggestion {
  title: string;
  cluster?: string;
  kind?: string;
  score?: number;
  rationale?: string;
  pastPieceSlug?: string;
  topAxes?: Array<{ name: string; score: number; reason?: string }>;
  // #178: analytics-seeded suggestions carry their source page's lifetime metrics.
  seedSource?: "analytics" | "gap" | "signals" | "template";
  analyticsOrigin?: {
    sourceSlug: string;
    views: number;
    impressions: number;
    clicks: number;
    cluster: string;
  };
}

interface LanesResponse {
  trending: SocialTopicSuggestion[];
  past: SocialTopicSuggestion[];
  mode: string;
}

/**
 * Social Studio — picks a topic FIRST, then platform → format → result.
 *
 * Topic step shows three lanes:
 *   1. Trending — HN + Reddit signal-informed, top 5
 *   2. From past articles — corpus-aware gap-fills + thread continuations,
 *      each tied to a real past piece's narrative.md
 *   3. Manual free-text input
 *
 * Picking a "from past" topic uses that piece's narrative as the source.
 * Picking a trending or manual topic creates a scratch piece behind the
 * scenes (no user-visible scratch flow).
 */
export default function SocialStudioView({ brand, pieceSlug, pieces, onScratchPieceCreated }: SocialStudioViewProps) {
  const [step, setStep] = useState<Step>('topic');
  const [activeSlug, setActiveSlug] = useState<string | null>(pieceSlug);
  const [platform, setPlatform] = useState<Platform | null>(null);
  const [formats, setFormats] = useState<SocialFormatOption[]>([]);
  const [formatId, setFormatId] = useState<string | null>(null);
  const [topic, setTopic] = useState('');
  const [topicSource, setTopicSource] = useState<'trending' | 'past' | 'manual' | null>(null);
  const [trending, setTrending] = useState<SocialTopicSuggestion[]>([]);
  const [past, setPast] = useState<SocialTopicSuggestion[]>([]);
  const [suggestionsMode, setSuggestionsMode] = useState<string>('');
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [freeTextCluster, setFreeTextCluster] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<CreateResponse | null>(null);
  /** Brief confirmation shown after a successful Regenerate so the user sees
   *  that the action did something — issue #117. Cleared on next submit. */
  const [regenNote, setRegenNote] = useState<string | null>(null);
  // #305: pasted primary source for a poll (verifies the caption stat).
  const [pollSourceText, setPollSourceText] = useState('');
  const [pollSourceUrl, setPollSourceUrl] = useState('');
  // Cross-post mode — when true, format step is multi-select and submit
  // hits /social/cross-create. Per issue #110.
  const [crossPost, setCrossPost] = useState(false);
  const [crossFormatIds, setCrossFormatIds] = useState<string[]>([]);
  const [crossResults, setCrossResults] = useState<Array<{ formatId: string; ok: boolean; result?: CreateResponse; error?: string }>>([]);
  const [crossActiveIdx, setCrossActiveIdx] = useState(0);

  const selectedFormat = useMemo(
    () => formats.find((f) => f.id === formatId) ?? null,
    [formats, formatId],
  );

  // Fetch format catalog once.
  useEffect(() => {
    let alive = true;
    fetch(`/api/${brand}/social/formats`)
      .then((r) => r.json())
      .then((d) => { if (alive) setFormats(d.formats ?? []); })
      .catch(() => { /* not fatal — picker step shows error inline */ });
    return () => { alive = false; };
  }, [brand]);

  // Fetch lanes on mount + when brand changes.
  const loadLanes = useCallback(async (includeSignals: boolean) => {
    setLoadingSuggestions(true);
    setError(null);
    try {
      const r = await fetch(`/api/${brand}/social/suggest-topics`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ count: 5, includeSignals }),
      });
      const d: LanesResponse = await r.json();
      setTrending(d.trending ?? []);
      setPast(d.past ?? []);
      setSuggestionsMode(d.mode ?? '');
    } catch (e) {
      setError(`Failed to load topic suggestions: ${String(e)}`);
    } finally {
      setLoadingSuggestions(false);
    }
  }, [brand]);

  useEffect(() => { void loadLanes(true); }, [loadLanes]);

  // Deep-link from Library: when SocialStudio mounts with a pieceSlug (the
  // user clicked the per-row "Social" button), skip the topic-picker step
  // and land on the platform-pick step with the piece already attached.
  // Mirrors the "past article" lane — sets topicSource so submit() reuses
  // the existing piece's narrative.md instead of creating a scratch piece.
  // Keyed by slug via ref so a second click on a different piece re-fires,
  // but clicking the same piece twice or pressing "Back to topic" doesn't.
  const lastDeepLinkSlug = useRef<string | null>(null);
  useEffect(() => {
    if (!pieceSlug) return;
    if (lastDeepLinkSlug.current === pieceSlug) return;
    lastDeepLinkSlug.current = pieceSlug;
    const title = pieces?.find((p) => p.slug === pieceSlug)?.title;
    setActiveSlug(pieceSlug);
    setTopic(title?.trim() || pieceSlug);
    setTopicSource('past');
    setStep('platform');
  }, [pieceSlug, pieces]);

  // Debounced classify of the manual topic so the cluster hint stays live.
  useEffect(() => {
    if (!topic.trim() || topicSource !== 'manual') { setFreeTextCluster(null); return; }
    const handle = setTimeout(() => {
      fetch(`/api/${brand}/social/classify-topic`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ topic }),
      })
        .then((r) => r.json())
        .then((d) => setFreeTextCluster(d.cluster ?? null))
        .catch(() => setFreeTextCluster(null));
    }, 300);
    return () => clearTimeout(handle);
  }, [topic, topicSource, brand]);

  const reset = () => {
    setStep('topic');
    setPlatform(null);
    setFormatId(null);
    setTopic('');
    setTopicSource(null);
    setActiveSlug(pieceSlug);
    setResult(null);
    setError(null);
  };

  /**
   * Advance from the topic step to the platform step. For "past" picks we
   * reuse the existing piece's narrative; for trending/manual we silently
   * create a scratch piece and use that slug as the source.
   */
  const commitTopic = async (
    picked: SocialTopicSuggestion | null,
    manualText: string,
    source: 'trending' | 'past' | 'manual',
  ): Promise<void> => {
    const finalTopic = picked?.title ?? manualText.trim();
    if (!finalTopic) { setError('Enter or pick a topic first.'); return; }
    setError(null);
    setBusy(true);
    try {
      let nextSlug: string | null = null;
      if (source === 'past' && picked?.pastPieceSlug) {
        nextSlug = picked.pastPieceSlug;
      } else {
        // Trending or manual → spin up a scratch piece silently.
        const r = await fetch(`/api/${brand}/social/scratch-piece`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ topic: finalTopic }),
        });
        const d = await r.json();
        if (!r.ok || d.error) { setError(d.error ?? `Scratch failed (${r.status})`); return; }
        nextSlug = d.slug;
        if (onScratchPieceCreated && nextSlug) onScratchPieceCreated(nextSlug);
      }
      setActiveSlug(nextSlug);
      setTopic(finalTopic);
      setTopicSource(source);
      setStep('platform');
    } catch (e) {
      setError(String(e));
    } finally {
      setBusy(false);
    }
  };

  const submit = async (regenerate: boolean): Promise<void> => {
    if (!activeSlug) { setError('Pick a topic first.'); return; }
    if (!topic.trim()) { setError('Topic missing — go back and re-pick.'); return; }

    // Cross-post path — issue #110.
    if (crossPost) {
      if (crossFormatIds.length < 2) { setError('Pick at least 2 formats for cross-post.'); return; }
      setError(null); setBusy(true);
      try {
        const r = await fetch(`/api/${brand}/social/cross-create`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ slug: activeSlug, formatIds: crossFormatIds, topic }),
        });
        const d = await r.json();
        if (!r.ok || d.error) { setError(d.error ?? `Request failed (${r.status})`); return; }
        setCrossResults(d.results ?? []);
        const firstOk = (d.results ?? []).findIndex((x: { ok: boolean }) => x.ok);
        setCrossActiveIdx(firstOk >= 0 ? firstOk : 0);
        const firstResult = (d.results ?? []).find((x: { ok: boolean; result?: CreateResponse }) => x.ok)?.result;
        if (firstResult) setResult(firstResult);
        setStep('result');
      } catch (e) {
        setError(String(e));
      } finally { setBusy(false); }
      return;
    }

    if (!formatId) { setError('Pick a format first.'); return; }
    setError(null);
    setRegenNote(null);
    setBusy(true);
    // Capture previous hash BEFORE the await so the closure isn't stale.
    // Used to detect "LLM returned the same content" on regenerate (rare but
    // possible with low-temperature renderers) and tell the user explicitly.
    const prevHash = regenerate ? result?.rendition.contentHash ?? null : null;
    try {
      const endpoint = regenerate ? 'regenerate' : 'create';
      // #305: for a poll, send the pasted primary source so the eval verifies
      // the caption's stat word-for-word against it (hard gate when supplied).
      const pollSource = formatId === 'linkedin-poll' && pollSourceText.trim()
        ? { pollSource: { text: pollSourceText.trim(), url: pollSourceUrl.trim() || undefined } }
        : {};
      const r = await fetch(`/api/${brand}/social/${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ slug: activeSlug, formatId, topic, ...pollSource }),
      });
      const d = await r.json();
      if (!r.ok || d.error) { setError(d.error ?? `Request failed (${r.status})`); return; }
      setResult(d);
      setCrossResults([]);
      setStep('result');
      if (regenerate) {
        const newHash = (d as CreateResponse).rendition.contentHash;
        const note = prevHash && newHash === prevHash
          ? `Regenerated, but the LLM returned identical content (hash ${newHash.slice(0, 8)}). Try a different topic or click Regenerate again.`
          : `Regenerated · new hash ${newHash.slice(0, 8)}`;
        setRegenNote(note);
        setTimeout(() => setRegenNote(null), 4000);
      }
    } catch (e) {
      setError(String(e));
    } finally {
      setBusy(false);
    }
  };

  const copyToClipboard = () => {
    if (!result) return;
    void navigator.clipboard.writeText(result.rendition.content);
  };

  // Build a self-contained HTML document for the current rendition, for the
  // Download-HTML + Preview actions. Carousel → each slide as an <img> (SVG via
  // a base64 data-URI so any smuggled <script> can't run) + caption/hashtags;
  // thread → numbered tweets; text → the raw content in a <pre>. All dynamic
  // text is HTML-escaped.
  const buildRenditionHtml = (): string => {
    if (!result) return '';
    const esc = (s: string) =>
      s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
    const kind = selectedFormat?.renderer ?? null;
    const label = esc(selectedFormat?.label ?? result.formatId);
    const carousel = kind === 'carousel' ? tryParseCarousel(result.rendition.content) : null;
    const thread = kind === 'thread' ? tryParseThread(result.rendition.content) : null;
    const poll = kind === 'poll' ? tryParsePoll(result.rendition.content) : null;

    let body: string;
    if (carousel) {
      const slides = carousel.slides
        .map((s, i) => {
          const src = isSvgSlide(s)
            ? `data:image/svg+xml;base64,${btoa(unescape(encodeURIComponent(s)))}`
            : `data:image/png;base64,${s}`;
          return `<figure class="slide"><img src="${src}" alt="Slide ${i + 1}"><figcaption>Slide ${i + 1} / ${carousel.slides.length}</figcaption></figure>`;
        })
        .join('\n');
      const tags = carousel.hashtags.length
        ? `<p class="tags">${carousel.hashtags.map((h) => '#' + esc(h)).join(' ')}</p>`
        : '';
      body = `<div class="slides">${slides}</div><section class="caption"><h2>Caption</h2><p>${esc(carousel.caption)}</p>${tags}</section>`;
    } else if (thread) {
      body = `<ol class="thread">${thread.tweets
        .map((t) => `<li><div class="meta">Tweet ${t.n}/${thread.tweets.length} · ${t.text.length} chars</div><div>${esc(t.text)}</div></li>`)
        .join('\n')}</ol>`;
    } else if (poll) {
      const opts = poll.options.map((o) => `<li>${esc(o)}</li>`).join('');
      const tags = poll.hashtags?.length ? `<p class="tags">${poll.hashtags.map((h) => '#' + esc(h)).join(' ')}</p>` : '';
      body = `<section class="caption"><h2>Caption</h2><p>${esc(poll.caption)}</p></section>`
        + `<section class="caption"><h2>Poll</h2><p><strong>${esc(poll.question)}</strong></p><ol class="poll">${opts}</ol><p class="meta">Duration: ${esc(pollDurationLabel(poll.durationDays))} · ${poll.options.length} options</p></section>`
        + `<section class="caption"><h2>Follow-up comment</h2><p>${esc(poll.comment)}</p>${tags}</section>`;
    } else {
      body = `<pre>${esc(result.rendition.content)}</pre>`;
    }

    return `<!doctype html><html lang="en"><head><meta charset="utf-8"><meta name="viewport" content="width=device-width, initial-scale=1"><title>${label}</title><style>
      body{font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;max-width:900px;margin:32px auto;padding:0 20px;color:#1c1a17;background:#faf7ec}
      h1{font-size:20px;letter-spacing:-.02em}
      .slides{display:grid;grid-template-columns:repeat(auto-fill,minmax(240px,1fr));gap:16px}
      .slide{margin:0;border:1px solid #ddd6c2;border-radius:8px;overflow:hidden;background:#fff}
      .slide img{display:block;width:100%;height:auto;aspect-ratio:1/1;object-fit:cover}
      .slide figcaption{padding:6px 8px;font-size:11px;color:#8a8578;border-top:1px solid #ddd6c2}
      .caption{margin-top:24px;padding:16px;border:1px solid #ddd6c2;border-radius:8px;background:#fff;white-space:pre-wrap}
      .caption h2{font-size:13px;text-transform:uppercase;letter-spacing:.08em;color:#8a8578;margin:0 0 8px}
      .tags{color:#2a5db0;margin-top:12px}
      .thread{list-style:none;padding:0;display:grid;gap:12px}
      .thread li{border:1px solid #ddd6c2;border-radius:8px;padding:12px;background:#fff;white-space:pre-wrap}
      .thread .meta{font-size:11px;color:#8a8578;margin-bottom:6px}
      pre{white-space:pre-wrap;padding:16px;border:1px solid #ddd6c2;border-radius:8px;background:#fff}
    </style></head><body><h1>${label}</h1>${body}</body></html>`;
  };

  const downloadRenditionHtml = () => {
    if (!result) return;
    const blob = new Blob([buildRenditionHtml()], { type: 'text/html;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${result.formatId || 'post'}-${result.rendition.contentHash.slice(0, 8)}.html`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const previewRenditionHtml = () => {
    if (!result) return;
    const w = window.open('', '_blank');
    if (!w) return; // popup blocked — no-op rather than throw
    w.document.open();
    w.document.write(buildRenditionHtml());
    w.document.close();
  };

  // Download a carousel as a PDF (one slide per page) — the format LinkedIn wants
  // for document/carousel posts. Only meaningful for the carousel renderer.
  const [pdfBusy, setPdfBusy] = useState(false);
  const base64ToBytes = (b64: string): Uint8Array =>
    Uint8Array.from(atob(b64), (c) => c.charCodeAt(0));
  // Rasterize a self-contained SVG slide to PNG bytes via a canvas (pdf-lib can
  // only embed PNG/JPG, not SVG). Carousel SVGs are self-contained, so this is safe.
  const svgToPngBytes = (svg: string): Promise<Uint8Array> =>
    new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        const w = img.naturalWidth || 1080;
        const h = img.naturalHeight || 1080;
        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');
        if (!ctx) { reject(new Error('no 2d context')); return; }
        ctx.drawImage(img, 0, 0, w, h);
        resolve(base64ToBytes(canvas.toDataURL('image/png').split(',')[1]));
      };
      img.onerror = () => reject(new Error('svg rasterize failed'));
      img.src = `data:image/svg+xml;base64,${btoa(unescape(encodeURIComponent(svg)))}`;
    });

  const downloadCarouselPdf = async () => {
    if (!result || pdfBusy) return;
    const parsed = tryParseCarousel(result.rendition.content);
    if (!parsed || parsed.slides.length === 0) { setError('No carousel slides to export.'); return; }
    setPdfBusy(true);
    try {
      // Lazy-load pdf-lib so it's a separate chunk, off the main bundle.
      const { PDFDocument } = await import('pdf-lib');
      const doc = await PDFDocument.create();
      for (const slide of parsed.slides) {
        const png = isSvgSlide(slide) ? await svgToPngBytes(slide) : base64ToBytes(slide);
        const img = await doc.embedPng(png);
        const page = doc.addPage([img.width, img.height]);
        page.drawImage(img, { x: 0, y: 0, width: img.width, height: img.height });
      }
      const bytes = await doc.save();
      // .slice().buffer → a plain ArrayBuffer (a valid BlobPart; the raw
      // Uint8Array<ArrayBufferLike> from pdf-lib doesn't satisfy the lib type).
      const blob = new Blob([bytes.slice().buffer], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${result.formatId || 'carousel'}-${result.rendition.contentHash.slice(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(`PDF export failed: ${(e as Error).message}`);
    } finally {
      setPdfBusy(false);
    }
  };

  const topAxesForResult = useMemo(() => {
    const axes = result?.perFormatScore?.axes ?? {};
    return Object.entries(axes)
      .map(([name, score]) => ({ name, score: Number(score) }))
      .sort((a, b) => a.score - b.score) // worst first — actionable
      .slice(0, 3);
  }, [result]);

  return (
    <div className="social-studio" data-testid="social-studio-view" style={{ padding: 24, display: 'grid', gap: 20, maxWidth: 1200 }}>
      <header style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
        <h2 style={{ margin: 0, letterSpacing: '-0.02em' }}>Social Studio</h2>
        <span className="label" style={{ fontFamily: 'var(--mono)', fontSize: 11, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--g-600)' }}>
          Step · {step}
        </span>
      </header>

      {error && (
        <div role="alert" className="alert alert--critical">
          <div className="alert__body">
            <div className="alert__title">{error}</div>
          </div>
        </div>
      )}

      {/* ── 1. TOPIC ────────────────────────────────────────────────── */}
      {step === 'topic' && (
        <TopicStep
          brand={brand}
          loading={loadingSuggestions}
          mode={suggestionsMode}
          trending={trending}
          past={past}
          manualTopic={topic}
          setManualTopic={(t) => { setTopic(t); setTopicSource('manual'); }}
          manualCluster={freeTextCluster}
          onRefresh={() => void loadLanes(true)}
          onPick={(picked, source) => void commitTopic(picked, picked?.title ?? '', source)}
          onCommitManual={() => void commitTopic(null, topic, 'manual')}
          busy={busy}
        />
      )}

      {/* ── 2. PLATFORM ─────────────────────────────────────────────── */}
      {step === 'platform' && (
        <section data-testid="step-platform" style={{ display: 'grid', gap: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button type="button" onClick={() => setStep('topic')} className="btn-ghost">← back</button>
            <div style={{ fontSize: 13, color: 'var(--g-600)' }}>
              Topic: <strong>{topic}</strong>
            </div>
          </div>
          <div className="sec-meta" style={{ marginBottom: 4 }}>Pick a platform</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            {(['linkedin', 'x'] as Platform[]).map((p) => (
              <button
                key={p}
                type="button"
                data-testid={`platform-${p}`}
                onClick={() => { setPlatform(p); setStep('format'); }}
                className="card-lift"
                style={{ textAlign: 'left', padding: 20 }}
              >
                <div style={{ fontSize: 18, fontWeight: 600 }}>
                  {p === 'linkedin' ? 'LinkedIn' : 'X (Twitter)'}
                </div>
                <div className="label" style={{ marginTop: 8 }}>
                  {p === 'linkedin' ? '6 formats · long-form-friendly' : '4 formats · short + thread'}
                </div>
              </button>
            ))}
          </div>
        </section>
      )}

      {/* ── 3. FORMAT ───────────────────────────────────────────────── */}
      {step === 'format' && platform && (
        <section data-testid="step-format" style={{ display: 'grid', gap: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <button type="button" onClick={() => setStep('platform')} className="btn-ghost">← back</button>
              <div style={{ fontSize: 13, color: 'var(--g-600)' }}>
                <strong>{platform === 'linkedin' ? 'LinkedIn' : 'X'}</strong> · {topic}
              </div>
            </div>
            {/* Cross-post toggle (#110) */}
            <div className="segmented" role="group" aria-label="Cross-post mode">
              <button
                type="button"
                className={`seg ${!crossPost ? 'active' : ''}`}
                onClick={() => { setCrossPost(false); setCrossFormatIds([]); }}
                data-testid="mode-single"
              >Single</button>
              <button
                type="button"
                className={`seg ${crossPost ? 'active' : ''}`}
                onClick={() => { setCrossPost(true); setFormatId(null); }}
                data-testid="mode-cross"
              >Cross-post</button>
            </div>
          </div>
          {crossPost && (
            <div style={{ fontSize: 12, color: 'var(--g-600)', fontFamily: 'var(--mono)', letterSpacing: '0.04em' }}>
              Pick 2–6 formats. Each renders in parallel from the same topic + narrative.
            </div>
          )}
          <PostTypePicker
            formats={formats}
            platform={platform}
            selectedId={formatId}
            onSelect={setFormatId}
            multi={crossPost}
            selectedIds={crossFormatIds}
            onToggle={(id) => setCrossFormatIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id])}
          />
          {formatId === 'linkedin-poll' && !crossPost && (
            <div data-testid="poll-source" style={{ display: 'grid', gap: 6, padding: 12, border: '1px solid var(--border)', borderRadius: 0, background: 'var(--bg-surface)' }}>
              <label style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                Primary source (optional): paste the exact source text your stat comes from. When provided, the poll eval verifies the caption stat word-for-word against it and blocks publish if it is not found.
              </label>
              <textarea
                data-testid="poll-source-text"
                value={pollSourceText}
                onChange={(e) => setPollSourceText(e.target.value)}
                placeholder="Paste the source excerpt containing the exact stat (e.g. the IDC / Gartner sentence)…"
                rows={4}
                style={{ width: '100%', fontFamily: 'inherit', fontSize: 13, padding: 8, border: '1px solid var(--border)', borderRadius: 0, background: 'var(--bg-base)', color: 'var(--text-primary)' }}
              />
              <input
                data-testid="poll-source-url"
                type="url"
                value={pollSourceUrl}
                onChange={(e) => setPollSourceUrl(e.target.value)}
                placeholder="Source URL (optional)"
                style={{ width: '100%', fontFamily: 'inherit', fontSize: 13, padding: 8, border: '1px solid var(--border)', borderRadius: 0, background: 'var(--bg-base)', color: 'var(--text-primary)' }}
              />
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button
              type="button"
              data-testid="submit-create"
              onClick={() => void submit(false)}
              disabled={busy || !activeSlug || (crossPost ? crossFormatIds.length < 2 : !formatId)}
              className="btn btn-primary"
            >
              {busy ? 'Creating…' : crossPost ? `Cross-post (${crossFormatIds.length})` : 'Create'}
            </button>
          </div>
        </section>
      )}

      {/* ── 4. RESULT ───────────────────────────────────────────────── */}
      {step === 'result' && result && (
        <section data-testid="step-result" style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 16 }}>
          <div style={{ display: 'grid', gap: 12 }}>
            {/* Cross-post tabs — visible only when crossResults has entries */}
            {crossResults.length > 1 && (
              <div className="tabs" data-testid="cross-tabs">
                {crossResults.map((r, i) => {
                  const lbl = formats.find((f) => f.id === r.formatId)?.label ?? r.formatId;
                  return (
                    <button
                      key={r.formatId}
                      type="button"
                      className={`tab ${i === crossActiveIdx ? 'active' : ''}`}
                      onClick={() => {
                        setCrossActiveIdx(i);
                        if (r.ok && r.result) setResult(r.result);
                      }}
                    >
                      {lbl} {r.ok ? '' : '· failed'}
                    </button>
                  );
                })}
              </div>
            )}
            <header style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {/* #238: go back to the format picker without resetting topic/platform.
                    The format step keeps `platform`, `topic`, `formats` and the current
                    `formatId`, so the user can pick a different form and re-Create. */}
                <button
                  type="button"
                  data-testid="back-to-format"
                  onClick={() => { setStep('format'); setRegenNote(null); }}
                  className="btn-ghost"
                  title="Pick a different format — keeps your topic and platform"
                >← Format</button>
                <h3 style={{ margin: 0, letterSpacing: '-0.02em' }}>{selectedFormat?.label ?? result.formatId}</h3>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button type="button" data-testid="copy-clipboard" onClick={copyToClipboard} className="btn btn-secondary">Copy</button>
                <button type="button" data-testid="preview-html" onClick={previewRenditionHtml} className="btn btn-secondary" title="Open a rendered HTML preview in a new tab">Preview</button>
                <button type="button" data-testid="download-html" onClick={downloadRenditionHtml} className="btn btn-secondary" title="Download this post as a self-contained HTML file">Download HTML</button>
                {selectedFormat?.renderer === 'carousel' && (
                  <button type="button" data-testid="download-pdf" onClick={() => void downloadCarouselPdf()} disabled={pdfBusy} className="btn btn-secondary" title="Download the carousel as a PDF (one slide per page) — LinkedIn's document/carousel format">
                    {pdfBusy ? 'Building PDF…' : 'Download PDF'}
                  </button>
                )}
                <button type="button" data-testid="regenerate" onClick={() => void submit(true)} disabled={busy || crossPost} className="btn btn-secondary" title={crossPost ? 'Regenerate from the format step in cross-post mode' : undefined}>
                  {busy ? 'Regenerating…' : 'Regenerate'}
                </button>
                <button type="button" onClick={reset} className="btn btn-ghost">New</button>
              </div>
            </header>
            {/* Regenerate confirmation banner — issue #117. Tells the user the
                click landed, even when the LLM produced near-identical content. */}
            {regenNote && (
              <div role="status" data-testid="regen-note" className="alert" style={{ padding: '8px 12px', fontSize: 13 }}>
                {regenNote}
              </div>
            )}
            <PostPreview formatId={result.formatId} rendererKind={selectedFormat?.renderer ?? null} content={result.rendition.content} />
          </div>
          <aside style={{ display: 'grid', gap: 12 }}>
            <EvalScoreCard
              label="Post score"
              score={result.evalScore}
              grade={result.evalGrade}
              axes={topAxesForResult}
            />
            <div style={{ fontSize: 11, color: 'var(--g-500)', fontFamily: 'var(--mono)' }}>
              Rendered {new Date(result.rendition.renderedAt).toLocaleString()}<br />
              Hash {result.rendition.contentHash.slice(0, 8)}
            </div>
          </aside>
        </section>
      )}
    </div>
  );
}

/* ── Topic step — two lanes + manual ───────────────────────────────────── */

interface TopicStepProps {
  brand: string;
  loading: boolean;
  mode: string;
  trending: SocialTopicSuggestion[];
  past: SocialTopicSuggestion[];
  manualTopic: string;
  setManualTopic: (t: string) => void;
  manualCluster: string | null;
  onRefresh: () => void;
  onPick: (s: SocialTopicSuggestion, source: 'trending' | 'past') => void;
  onCommitManual: () => void;
  busy: boolean;
}

function TopicStep({
  loading,
  mode,
  trending,
  past,
  manualTopic,
  setManualTopic,
  manualCluster,
  onRefresh,
  onPick,
  onCommitManual,
  busy,
}: TopicStepProps) {
  return (
    <section data-testid="step-topic" style={{ display: 'grid', gap: 20 }}>
      {/* Manual input — visually prominent */}
      <div data-testid="topic-manual" style={{ border: '1px solid var(--line)', padding: 16 }}>
        <div className="sec-meta" style={{ marginBottom: 10 }}>Write your own topic</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 10, alignItems: 'center' }}>
          <input
            type="text"
            value={manualTopic}
            onChange={(e) => setManualTopic(e.target.value)}
            placeholder="e.g. EKS right-sizing — measure first, then tune"
            className="input"
            data-testid="topic-input"
          />
          <button
            type="button"
            className="btn btn-primary"
            data-testid="topic-manual-submit"
            onClick={onCommitManual}
            disabled={busy || !manualTopic.trim()}
          >
            {busy ? 'Working…' : 'Use this topic'}
          </button>
        </div>
        {manualTopic.trim().length > 0 && (
          <div data-testid="manual-cluster" style={{ marginTop: 8, fontSize: 12, color: 'var(--g-600)', fontFamily: 'var(--mono)', letterSpacing: '0.04em' }}>
            {manualCluster
              ? <>Detected cluster: <strong>{manualCluster}</strong></>
              : <>Detected cluster: <em>unclassified — will fall back to brand defaults</em></>}
          </div>
        )}
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
        <div className="label">Or pick a suggested topic</div>
        <div style={{ display: 'flex', gap: 12, alignItems: 'baseline' }}>
          <span style={{ fontSize: 11, color: 'var(--g-500)', fontFamily: 'var(--mono)' }}>
            mode · {mode || (loading ? 'loading' : 'template')}
          </span>
          <button type="button" onClick={onRefresh} className="btn-ghost" style={{ fontSize: 12 }}>
            Refresh
          </button>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        <SuggestionLane
          testid="lane-trending"
          headline="Trending now"
          subhead="HN + Reddit · scoped to your clusters"
          empty="No trending hits — check signal connectivity or pick from past articles."
          loading={loading}
          items={trending}
          onPick={(s) => onPick(s, 'trending')}
          busy={busy}
        />
        <SuggestionLane
          testid="lane-past"
          headline="From your past articles"
          subhead="Gap-fills + thread continuations · corpus-aware"
          empty="No past-article suggestions — build a corpus index for this brand."
          loading={loading}
          items={past}
          onPick={(s) => onPick(s, 'past')}
          busy={busy}
        />
      </div>
    </section>
  );
}

/* ── Suggestion lane (one column) ─────────────────────────────────────── */

interface LaneProps {
  testid: string;
  headline: string;
  subhead: string;
  empty: string;
  loading: boolean;
  items: SocialTopicSuggestion[];
  onPick: (s: SocialTopicSuggestion) => void;
  busy: boolean;
}

function SuggestionLane({ testid, headline, subhead, empty, loading, items, onPick, busy }: LaneProps) {
  return (
    <div data-testid={testid} style={{ display: 'grid', gap: 10 }}>
      <div>
        <div style={{ fontSize: 14, fontWeight: 600, letterSpacing: '-0.01em' }}>{headline}</div>
        <div className="label" style={{ marginTop: 2 }}>{subhead}</div>
      </div>
      {loading && items.length === 0 && (
        <>
          <div className="skeleton" />
          <div className="skeleton" />
          <div className="skeleton" />
          <div className="skeleton" />
          <div className="skeleton" />
        </>
      )}
      {!loading && items.length === 0 && (
        <div style={{ fontSize: 12, color: 'var(--g-500)', padding: '12px 0' }}>{empty}</div>
      )}
      {items.map((s) => (
        <button
          key={s.title}
          type="button"
          data-testid="suggestion-card"
          onClick={() => onPick(s)}
          disabled={busy}
          className="card-lift"
          style={{ textAlign: 'left', display: 'grid', gap: 6, cursor: busy ? 'not-allowed' : 'pointer' }}
        >
          <div style={{ fontWeight: 500, lineHeight: 1.35 }}>{s.title}</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {/* #178: all-time top-performer seed badge */}
            {s.seedSource === 'analytics' && (
              <span
                className="pill"
                data-color="orange"
                title={s.analyticsOrigin
                  ? `${s.analyticsOrigin.views.toLocaleString()} lifetime views · ${s.analyticsOrigin.impressions.toLocaleString()} lifetime impressions · cluster: ${s.analyticsOrigin.cluster}`
                  : 'Seeded from an all-time top-performing page'}
              >
                🔥 all-time top performer
              </span>
            )}
            {s.cluster && <span className="pill" data-color="orange">{s.cluster}</span>}
            {s.kind && <span className="pill">{s.kind}</span>}
            {typeof s.score === 'number' && <span className="pill">score {s.score}</span>}
          </div>
          {s.topAxes && s.topAxes.length > 0 && (
            <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'grid', gap: 2 }}>
              {s.topAxes.slice(0, 3).map((a) => (
                <li key={a.name} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'var(--g-600)', fontFamily: 'var(--mono)' }}>
                  <span>{a.name}</span>
                  <span style={{ color: 'var(--ink)', fontVariantNumeric: 'tabular-nums' }}>{a.score}</span>
                </li>
              ))}
            </ul>
          )}
          {s.pastPieceSlug && (
            <div style={{ fontSize: 11, color: 'var(--g-500)', fontFamily: 'var(--mono)', letterSpacing: '0.04em' }}>
              ← from /{s.pastPieceSlug}
            </div>
          )}
        </button>
      ))}
    </div>
  );
}
